/**
 * This file contains everything needed to create the model viewer HTML element to be appended to the DOM
 */

const whiteImage =
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=\n';

export const createModelViewerStyle = (primaryThemeColor: string) => `
<style>
#controls {
position: absolute;
bottom: 16px;
left: 16px;
max-width: unset;
transform: unset;
pointer-events: auto;
}

.dimensionLineContainer{
pointer-events: none;
position: fixed;
display: block;
width: 100%;
height: 100%; 
}

.dimensionLine{
stroke: ${primaryThemeColor};
stroke-width: 2;
stroke-dasharray: 5
}

.dot{
display: block;
width: 12px;
height: 12px;
border-radius: 50%;
border: none;
box-sizing: border-box;
background: ${primaryThemeColor};
pointer-events: none;
--min-hotspot-opacity: 0;
padding: 0;
}

.dim{
background: #fff;
border-radius: 4px;
border: none;
box-sizing: border-box;
box-shadow: 0 2px 4px rgba(0, 0, 0, 0.25);
color: rgba(0, 0, 0, 0.8);
display: block;
font-family: 'Inter';
font-size: 13px;
font-weight: 700;
max-width: 128px;
overflow-wrap: break-word;
padding: 0.5em 1em;
position: absolute;
width: max-content;
height: max-content;
transform: translate3d(-50%, -50%, 0);
pointer-events: none;
--min-hotspot-opacity: 0;
--max-hotspot-opacity: 0.8;
}

.show{
--min-hotspot-opacity: 1;
}

.hide{
display: none;
}
/* This keeps child nodes hidden while the element loads */
:not(:defined) > * {
display: none;
}
</style>`;

/**
 * generate the inner HTML for the model viewer component. This does include an
 * invisible 'ar-button' to prevent the model viewer from creating the obnoxious
 * floating default.
 */
const createModelViewerHtml = () => `
<button slot="hotspot-dim+X-Y" id="dim+X-Y" class="dim" data-position="1 -1 0" data-normal="1 0 0"></button>
<button slot="hotspot-dim+X-Z" id="dim+X-Z" class="dim" data-position="1 0 -1" data-normal="1 0 0"></button>
<button slot="hotspot-dim+Y-Z" id="dim+Y-Z" class="dim show" data-position="0 -1 -1" data-normal="0 1 0"></button>
<button slot="hotspot-dim-X-Z" id="dim-X-Z" class="dim" data-position="-1 0 -1" data-normal="-1 0 0"></button>
<button slot="hotspot-dim-X-Y" id="dim-X-Y" class="dim" data-position="-1 -1 0" data-normal="-1 0 0"></button>
<button slot="hotspot-dot+X-Y+Z" id="dot+X-Y+Z" class="dot" data-position="1 -1 1" data-normal="1 0 0"></button>
<button slot="hotspot-dot+X-Y-Z" id="dot+X-Y-Z" class="dot" data-position="1 -1 -1" data-normal="1 0 0"></button>
<button slot="hotspot-dot+X+Y-Z" id="dot+X+Y-Z" class="dot show" data-position="1 1 -1" data-normal="0 1 0"></button>
<button slot="hotspot-dot-X+Y-Z" id="dot-X+Y-Z" class="dot show" data-position="-1 1 -1" data-normal="0 1 0"></button>
<button slot="hotspot-dot-X-Y-Z" id="dot-X-Y-Z" class="dot" data-position="-1 -1 -1" data-normal="-1 0 0"></button>
<button slot="hotspot-dot-X-Y+Z" id="dot-X-Y+Z" class="dot" data-position="-1 -1 1" data-normal="-1 0 0"></button>
<div slot="poster" style='opacity: 0'></div>
<div slot="progress-bar" style='opacity: 0'></div>
<button slot="ar-button" style='opacity: 0'></button>
<svg xmlns="http://www.w3.org/2000/svg" class="dimensionLineContainer">
<line class="dimensionLine"></line>
<line class="dimensionLine"></line>
<line class="dimensionLine"></line>
<line class="dimensionLine"></line>
<line class="dimensionLine"></line>
</svg>
`;

export const createModelViewerElement = (modelPreviewModeActive: boolean) => {
    // the included declarations cast to a type which does not include standard HTML node types so we need to cast back!
    const modelViewer = document.createElement('model-viewer');

    // Even though we override these later, they're used internally by model-viewer as an init sanity check.
    // Do not omit these!
    modelViewer.src = '';
    modelViewer.arPlacement = 'floor';
    modelViewer.alt = 'AR Scene';
    modelViewer.arModes = 'webxr scene-viewer quick-look';
    modelViewer.environmentImage = 'neutral';
    modelViewer.autoRotate = true;
    modelViewer.interactionPrompt = 'auto';
    modelViewer.cameraControls = true;
    modelViewer.setAttribute('style', 'background-color: unset; width: 100%; height: 100%;');
    modelViewer.setAttribute('data-testid', 'model-viewer-element');
    modelViewer.cameraOrbit = '-45deg 75deg 105%';
    modelViewer.ar = true;

    if (!modelPreviewModeActive) {
        // prevent model from automatically loading
        modelViewer.reveal = 'manual';
    }

    // show a white image as a "poster" (typically a low resolution render used for preloading)
    modelViewer.poster = whiteImage;
    modelViewer.setAttribute('poster', whiteImage);

    modelViewer.innerHTML = createModelViewerHtml();

    return modelViewer;
};
